import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface DocumentData {
  type: 'prescription' | 'bon_laboratoire' | 'resultat_analyse';
  id: string;
  date: string;
  patientNom: string;
  patientPrenom: string;
  prescripteurNom: string;
  prescripteurTitre: string;
  patientAge?: string;
  patientSexe?: string;
  // Prescription fields
  diagnostic?: string;
  duree_traitement?: string;
  instructions?: string;
  medicaments?: string[];
  // Bon laboratoire fields
  type_analyse?: string;
  description?: string;
  urgences?: boolean;
  observations_cliniques?: string;
  analyses_labels?: string[];
  // Resultat analyse fields
  resultats?: Array<{
    nom: string;
    code: string;
    valeur: string;
    unite: string;
    norme: string;
    statut: 'normal' | 'bas' | 'haut' | 'inconnu';
  }>;
  conclusion?: string;
  laborantinNom?: string;
}

export default function VerifierDocumentPage() {
  const [searchParams] = useSearchParams();
  const docType = searchParams.get('type');
  const docId = searchParams.get('id');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [docData, setDocData] = useState<DocumentData | null>(null);

  useEffect(() => {
    if (!docType || !docId) {
      setError('Paramètres de vérification manquants.');
      setLoading(false);
      return;
    }

    if (docType !== 'prescription' && docType !== 'bon_laboratoire' && docType !== 'resultat_analyse') {
      setError('Type de document invalide.');
      setLoading(false);
      return;
    }

    loadDocument();
  }, [docType, docId]);

  const loadDocument = async () => {
    setLoading(true);
    try {
      if (docType === 'prescription') {
        await loadPrescription();
      } else if (docType === 'bon_laboratoire') {
        await loadBonLaboratoire();
      } else {
        await loadResultatAnalyse();
      }
    } catch (err: any) {
      console.error('Erreur chargement document:', err);
      setError(err.message || 'Document introuvable ou expiré.');
    } finally {
      setLoading(false);
    }
  };

  const loadPrescription = async () => {
    const { data: prescription, error: prescError } = await supabase
      .from('prescriptions')
      .select('*')
      .eq('id', docId)
      .maybeSingle();

    if (prescError) throw prescError;
    if (!prescription) throw new Error('Prescription introuvable.');

    const { data: patient } = await supabase
      .from('patients')
      .select('nom, prenom')
      .eq('id', prescription.patient_id)
      .maybeSingle();

    const { data: medecin } = await supabase
      .from('medecins')
      .select('nom, prenom, specialite')
      .eq('id', prescription.medecin_id)
      .maybeSingle();

    const medicaments: string[] = prescription.medicaments || [];
    const prescriptionsTexte = medicaments.map((m: any) => {
      if (typeof m === 'string') return m;
      if (typeof m === 'object' && m.soin) return `${m.soin} — ${m.date || ''}`;
      return String(m);
    });

    const dateStr = prescription.date_prescription || prescription.created_at;
    const formattedDate = dateStr
      ? new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : 'Date inconnue';

    setDocData({
      type: 'prescription',
      id: prescription.id,
      date: formattedDate,
      patientNom: patient?.nom || 'Inconnu',
      patientPrenom: patient?.prenom || '',
      prescripteurNom: medecin ? `Dr. ${medecin.prenom} ${medecin.nom}` : 'Médecin',
      prescripteurTitre: medecin?.specialite || 'Médecin',
      diagnostic: prescription.diagnostic,
      duree_traitement: prescription.duree_traitement,
      instructions: prescription.instructions,
      medicaments: prescriptionsTexte,
    });
  };

  const loadBonLaboratoire = async () => {
    const { data: bon, error: bonError } = await supabase
      .from('bons_laboratoire')
      .select('*')
      .eq('id', docId)
      .maybeSingle();

    if (bonError) throw bonError;
    if (!bon) throw new Error('Bon de laboratoire introuvable.');

    const { data: patient } = await supabase
      .from('patients')
      .select('nom, prenom')
      .eq('id', bon.patient_id)
      .maybeSingle();

    let prescripteurNom = 'Prescripteur';
    let prescripteurTitre = '';

    if (bon.prescripteur_type === 'sage_femme' && bon.sage_femme_id) {
      const { data: sf } = await supabase
        .from('sage_femmes')
        .select('nom, prenom')
        .eq('id', bon.sage_femme_id)
        .maybeSingle();
      if (sf) {
        prescripteurNom = `Sage-femme ${sf.prenom} ${sf.nom}`;
        prescripteurTitre = 'Sage-femme';
      }
    } else if (bon.medecin_id) {
      const { data: medecin } = await supabase
        .from('medecins')
        .select('nom, prenom, specialite')
        .eq('id', bon.medecin_id)
        .maybeSingle();
      if (medecin) {
        prescripteurNom = `Dr. ${medecin.prenom} ${medecin.nom}`;
        prescripteurTitre = medecin.specialite || 'Médecin';
      }
    }

    const analysesLabels = bon.type_analyse
      ? bon.type_analyse.split(' ; ').filter(Boolean)
      : [];

    const dateStr = bon.date_prescription || bon.created_at;
    const formattedDate = dateStr
      ? new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : 'Date inconnue';

    setDocData({
      type: 'bon_laboratoire',
      id: bon.id,
      date: formattedDate,
      patientNom: patient?.nom || 'Inconnu',
      patientPrenom: patient?.prenom || '',
      prescripteurNom,
      prescripteurTitre,
      type_analyse: bon.type_analyse,
      description: bon.description,
      urgences: bon.urgences,
      observations_cliniques: bon.observations_cliniques,
      analyses_labels: analysesLabels,
    });
  };

  const loadResultatAnalyse = async () => {
    const { data: dossier, error: dossierError } = await supabase
      .from('dossiers_analyses')
      .select('*, patient_id, laborantin_id, date_analyse, conclusion')
      .eq('id', docId)
      .maybeSingle();

    if (dossierError) throw dossierError;
    if (!dossier) throw new Error('Résultat d\'analyse introuvable.');

    // Patient
    const { data: patient } = await supabase
      .from('patients')
      .select('nom, prenom, date_naissance, sexe')
      .eq('id', dossier.patient_id)
      .maybeSingle();

    // Biologiste médical
    let laborantinNom = 'Biologiste médical';
    if (dossier.laborantin_id) {
      const { data: labo } = await supabase
        .from('laborantins')
        .select('nom, prenom')
        .eq('id', dossier.laborantin_id)
        .maybeSingle();
      if (labo) {
        laborantinNom = `${labo.prenom} ${labo.nom}`;
      }
    }

    // Résultats
    const { data: resultats } = await supabase
      .from('resultats_analyses')
      .select(`
        valeur, valeur_texte,
        analyse:analyses_biochimie_catalogue!inner(
          code, nom, unite,
          val_min_adulte_homme, val_max_adulte_homme,
          val_min_adulte_femme, val_max_adulte_femme
        )
      `)
      .eq('dossier_id', dossier.id);

    const patientAge = patient?.date_naissance
      ? (() => {
          const birth = new Date(patient.date_naissance);
          const today = new Date();
          let age = today.getFullYear() - birth.getFullYear();
          const m = today.getMonth() - birth.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
          return age;
        })()
      : null;

    const patientAgeLabel = patientAge !== null ? `${patientAge} an${patientAge > 1 ? 's' : ''}` : '—';
    const patientSexeLabel = patient?.sexe === 'F' ? 'Féminin' : patient?.sexe === 'M' ? 'Masculin' : '—';

    const formattedResultats = (resultats || []).map((r: any) => {
      const analyse = r.analyse;
      const minRef = patient?.sexe === 'F' ? analyse?.val_min_adulte_femme : analyse?.val_min_adulte_homme;
      const maxRef = patient?.sexe === 'F' ? analyse?.val_max_adulte_femme : analyse?.val_max_adulte_homme;

      let normeStr = '—';
      if (minRef !== null && maxRef !== null) normeStr = `${minRef} – ${maxRef}`;
      else if (maxRef !== null) normeStr = `< ${maxRef}`;
      else if (minRef !== null) normeStr = `> ${minRef}`;

      const isTexte = r.valeur === null && r.valeur_texte !== null;
      const valeurStr = isTexte ? r.valeur_texte : r.valeur !== null ? `${r.valeur} ${analyse?.unite || ''}` : '—';

      let statut: 'normal' | 'bas' | 'haut' | 'inconnu' = 'inconnu';
      if (!isTexte && r.valeur !== null) {
        if (minRef !== null && r.valeur < minRef) statut = 'bas';
        else if (maxRef !== null && r.valeur > maxRef) statut = 'haut';
        else if (minRef !== null || maxRef !== null) statut = 'normal';
      }

      return {
        nom: analyse?.nom || 'Analyse',
        code: analyse?.code || '',
        valeur: valeurStr,
        unite: analyse?.unite || '',
        norme: `${normeStr} ${analyse?.unite || ''}`,
        statut,
      };
    });

    const dateStr = dossier.date_analyse || dossier.created_at;
    const formattedDate = dateStr
      ? new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : 'Date inconnue';

    setDocData({
      type: 'resultat_analyse',
      id: dossier.id,
      date: formattedDate,
      patientNom: patient?.nom || 'Inconnu',
      patientPrenom: patient?.prenom || '',
      patientAge: patientAgeLabel,
      patientSexe: patientSexeLabel,
      prescripteurNom: laborantinNom,
      prescripteurTitre: 'Biologiste médical',
      resultats: formattedResultats,
      conclusion: dossier.conclusion,
      laborantinNom,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-foreground-400 animate-spin"></i>
          <p className="mt-4 text-foreground-600">Vérification du document...</p>
        </div>
      </div>
    );
  }

  if (error || !docData) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-error-warning-line text-4xl text-accent-600"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-950 mb-3">Document introuvable</h1>
          <p className="text-foreground-600 mb-8">
            {error || 'Ce document n\'existe pas ou a expiré.'}
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            Retour à l&apos;accueil
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  const isPrescription = docData.type === 'prescription';
  const isBonLabo = docData.type === 'bon_laboratoire';
  const isResultatAnalyse = docData.type === 'resultat_analyse';

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      {/* Verified banner */}
      <div className="bg-accent-500 text-background-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-center gap-3">
          <div className="w-8 h-8 bg-background-50/20 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-check-line text-lg"></i>
          </div>
          <div>
            <p className="text-sm font-semibold">Document authentique vérifié</p>
            <p className="text-xs text-background-50/80">
              Ce document a été émis électroniquement par la clinique
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Document card */}
        <div className="bg-background-50 rounded-xl border-2 border-accent-200 p-6 md:p-10">
          {/* Document header */}
          <div className="text-center mb-8 pb-6 border-b-2 border-background-200">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent-100 text-accent-700 rounded-full text-xs font-semibold mb-4">
              <i className="ri-shield-check-line"></i>
              DOCUMENT AUTHENTIFIÉ
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {isPrescription ? 'Ordonnance' : isBonLabo ? 'Bon de Laboratoire' : 'Résultat d\'Analyse'}
            </h1>
            <p className="text-foreground-500 text-sm">
              Émis le {docData.date}
            </p>
          </div>

          {/* Meta info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <span className="block text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">Patient</span>
              <span className="text-foreground-950 font-medium">
                {docData.patientPrenom} {docData.patientNom}
              </span>
            </div>
            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <span className="block text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">Prescripteur</span>
              <span className="text-foreground-950 font-medium">{docData.prescripteurNom}</span>
              {docData.prescripteurTitre && (
                <span className="block text-foreground-500 text-xs">{docData.prescripteurTitre}</span>
              )}
            </div>
            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <span className="block text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">Référence</span>
              <span className="text-foreground-500 text-xs font-mono break-all">{docData.id}</span>
            </div>
          </div>

          {/* Prescription content */}
          {isPrescription && (
            <>
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-foreground-700 mb-3 flex items-center gap-2">
                  <i className="ri-medicine-bottle-line text-accent-500"></i>
                  Prescription médicale
                </h3>
                <div className="bg-background-50 border border-background-200 rounded-lg p-5 text-foreground-800 text-sm leading-relaxed whitespace-pre-wrap min-h-[100px]">
                  {docData.diagnostic || 'Aucune prescription détaillée.'}
                </div>
              </div>

              {docData.medicaments && docData.medicaments.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-3 flex items-center gap-2">
                    <i className="ri-capsule-line text-accent-500"></i>
                    Médicaments prescrits
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {docData.medicaments.map((m, i) => (
                      <span key={i} className="px-3 py-1.5 bg-accent-50 text-accent-700 text-sm rounded-full border border-accent-200">
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {docData.duree_traitement && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-2">Durée du traitement</h3>
                  <p className="text-foreground-800 text-sm bg-background-50 border border-background-200 rounded-lg p-4">
                    {docData.duree_traitement}
                  </p>
                </div>
              )}

              {docData.instructions && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-2">Instructions particulières</h3>
                  <p className="text-foreground-800 text-sm bg-background-50 border border-background-200 rounded-lg p-4 whitespace-pre-wrap">
                    {docData.instructions}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Bon laboratoire content */}
          {isBonLabo && (
            <>
              {docData.urgences && (
                <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-300 rounded-lg flex items-center gap-3">
                  <i className="ri-alert-fill text-amber-600 text-xl flex-shrink-0"></i>
                  <p className="text-amber-800 font-semibold text-sm">ANALYSE URGENTE — À traiter en priorité</p>
                </div>
              )}

              {docData.analyses_labels && docData.analyses_labels.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-3 flex items-center gap-2">
                    <i className="ri-flask-line text-accent-500"></i>
                    Analyses demandées
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {docData.analyses_labels.map((a, i) => (
                      <span key={i} className="px-3 py-1.5 bg-accent-50 text-accent-700 text-sm rounded-full border border-accent-200">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {docData.description && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-2">Description détaillée</h3>
                  <p className="text-foreground-800 text-sm bg-background-50 border border-background-200 rounded-lg p-4 whitespace-pre-wrap">
                    {docData.description}
                  </p>
                </div>
              )}

              {docData.observations_cliniques && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-2">Observations cliniques</h3>
                  <p className="text-foreground-800 text-sm bg-background-50 border border-background-200 rounded-lg p-4 whitespace-pre-wrap">
                    {docData.observations_cliniques}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Résultat analyse content */}
          {isResultatAnalyse && (
            <>
              {docData.patientAge && (
                <div className="mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-background-50 border border-background-200 rounded-lg p-4">
                      <span className="block text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">Âge</span>
                      <span className="text-foreground-950 font-medium">{docData.patientAge}</span>
                    </div>
                    <div className="bg-background-50 border border-background-200 rounded-lg p-4">
                      <span className="block text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">Sexe</span>
                      <span className="text-foreground-950 font-medium">{docData.patientSexe || '—'}</span>
                    </div>
                  </div>
                </div>
              )}

              {docData.resultats && docData.resultats.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-3 flex items-center gap-2">
                    <i className="ri-flask-line text-accent-500"></i>
                    Résultats des analyses
                  </h3>
                  <div className="overflow-x-auto rounded-lg border border-background-200">
                    <table className="w-full text-sm min-w-[420px]">
                      <thead>
                        <tr className="bg-background-100">
                          <th className="text-left px-4 py-2 text-xs font-bold text-foreground-600 uppercase">Test</th>
                          <th className="text-left px-4 py-2 text-xs font-bold text-foreground-600 uppercase">Résultat</th>
                          <th className="text-left px-4 py-2 text-xs font-bold text-foreground-600 uppercase">Normes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {docData.resultats.map((r, i) => (
                          <tr key={i} className={`border-t border-background-200 ${r.statut === 'haut' ? 'bg-red-50/30' : r.statut === 'bas' ? 'bg-amber-50/30' : ''}`}>
                            <td className="px-4 py-2.5">
                              <span className="font-semibold text-foreground-900">{r.nom}</span>
                              <span className="text-[10px] text-foreground-400 ml-1">({r.code})</span>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className={`font-bold ${r.statut === 'normal' || r.statut === 'inconnu' ? 'text-foreground-900' : r.statut === 'bas' ? 'text-amber-700' : 'text-red-700'}`}>
                                {r.valeur}
                              </span>
                              {r.statut === 'bas' && (
                                <span className="ml-2 text-[10px] font-bold text-amber-600">↓ BAS</span>
                              )}
                              {r.statut === 'haut' && (
                                <span className="ml-2 text-[10px] font-bold text-red-600">↑ HAUT</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-foreground-500">
                              {r.norme}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {docData.conclusion && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-2 flex items-center gap-2">
                    <i className="ri-file-text-line text-accent-500"></i>
                    Conclusion du biologiste médical
                  </h3>
                  <p className="text-foreground-800 text-sm bg-background-50 border border-background-200 rounded-lg p-4 whitespace-pre-wrap">
                    {docData.conclusion}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Footer stamp */}
          <div className="mt-10 pt-6 border-t-2 border-background-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center">
                  <i className="ri-shield-check-line text-accent-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground-700">Document électronique authentifié</p>
                  <p className="text-xs text-foreground-400">Vérifié le {new Date().toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-foreground-700">
                  {docData.prescripteurNom}
                </p>
                <p className="text-xs text-foreground-500">{docData.prescripteurTitre}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Back link */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-foreground-500 hover:text-primary-500 transition-colors text-sm cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Retour à l&apos;accueil
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
}